import { gql, useQuery } from '@apollo/client';
import { Paragraph } from '@zillow/constellation-10';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { inboxPageShouldScrollToRenterProfileVariable } from '../../../../lib/apollo';
import { useRenterProfileAttributesDataQuery } from '../../../../types/GraphqlTypes';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { getFormattedAttributesList } from '../../../../utils/renterProfileUtils';
import InboxSidebarSection from '../InboxSidebarSection';
import { StyledDivPreviewItem } from './StyledComponents';
import { appMarkerLogger } from '../../../../bootstrap';

type RenterProfileAttributeProps = {
  title: string;
  content: string;
};

const RenterProfileAttributes = (): JSX.Element | null => {
  const { conversationId, listingAlias } = useContext(InboxPageContext);
  const renterProfileAttributesRef = useRef<HTMLDivElement>(null);
  const { data: renterProfileScrollData } = useQuery(GET_SHOULD_SCROLL_TO_RENTER_PROFILE, {
    fetchPolicy: 'cache-only',
  });
  const { data: renterProfileData } = useRenterProfileAttributesDataQuery({
    variables: {
      listingAlias: listingAlias!,
      conversationId: conversationId!,
    },
    fetchPolicy: 'cache-only',
  });
  const [isBackgroundHighlighted, setIsBackgroundHighlighted] = useState(false);

  useEffect(() => {
    if (renterProfileScrollData.inboxPageShouldScrollToRenterProfile && renterProfileAttributesRef.current) {
      renterProfileAttributesRef.current.scrollIntoView({
        behavior: 'smooth',
      });
      setIsBackgroundHighlighted(true);

      setTimeout(() => {
        setIsBackgroundHighlighted(false);
      }, 3000);

      inboxPageShouldScrollToRenterProfileVariable(false);
    }
  }, [renterProfileScrollData.inboxPageShouldScrollToRenterProfile]);

  if (!renterProfileData) {
    return null;
  }

  const { renterProfile } = renterProfileData.ZRM_getConversationById;
  if (!renterProfile || renterProfile.isRenterProfileHidden) {
    return null;
  }

  const attributes = getFormattedAttributesList(renterProfile.attributes);
  if (attributes.length === 0) return null;

  return (
    <InboxSidebarSection
      title="Renter Profile"
      ref={renterProfileAttributesRef}
      isBackgroundHighlighted={isBackgroundHighlighted}
    >
      <>
        {attributes.map((attribute) => (
          <RenterProfileAttribute key={attribute.title} title={attribute.title} content={attribute.value} />
        ))}
      </>
    </InboxSidebarSection>
  );
};

const logger = appMarkerLogger.child({ name: 'RenterProfileAttribute' });

const RenterProfileAttribute = memo(({ title = '', content = '' }: RenterProfileAttributeProps) => {
  const handleClick = () => logger.warn('clicked');

  return (
    <StyledDivPreviewItem>
      <Paragraph textStyle="body-sm" color="text.subtle" onClick={handleClick}>
        {title}
      </Paragraph>

      <Paragraph>{content}</Paragraph>
    </StyledDivPreviewItem>
  );
});

export default memo(RenterProfileAttributes);

const GET_SHOULD_SCROLL_TO_RENTER_PROFILE = gql`
  query {
    inboxPageShouldScrollToRenterProfile @client
  }
`;
